import { useEffect, useRef, useState } from 'react';
import type { GeorefGcp } from '@/api/endpoints';

/** Prostokąt wycięcia pola mapy w pikselach rastra. */
export interface Crop { x: number; y: number; w: number; h: number }

export type Tryb = 'crop' | 'gcp' | 'trace';

/**
 * Płótno georeferencera: raster mapy + interaktywna warstwa SVG (wycięcie, punkty kontrolne, obrys).
 *
 * <p>Wszystkie współrzędne na zewnątrz są w PIKSELACH RASTRA — niezależnie od zoomu i rozmiaru okna.
 * Przeliczenie idzie przez prostokąt renderowanego obrazu, więc działa też po przewinięciu kontenera.
 */
export function GeorefCanvas({
  rasterUrl, width, height, tryb, crop, onCrop, gcps, onMoveGcp, onAddGcp, wybranyGcp, onWybierzGcp,
  obrys, onObrys, zoom,
}: {
  rasterUrl: string;
  width: number;
  height: number;
  tryb: Tryb;
  crop: Crop | null;
  onCrop: (c: Crop) => void;
  gcps: GeorefGcp[];
  onMoveGcp: (index: number, px: number, py: number) => void;
  onAddGcp: (px: number, py: number) => void;
  wybranyGcp: number | null;
  onWybierzGcp: (index: number | null) => void;
  obrys: number[][];
  onObrys: (pts: number[][]) => void;
  zoom: number;
}) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [drag, setDrag] = useState<
    | { typ: 'crop'; uchwyt: 'nw' | 'ne' | 'se' | 'sw' | 'move'; start: Crop; sx: number; sy: number }
    | { typ: 'gcp'; index: number }
    | null>(null);

  /** Punkt zdarzenia myszy w pikselach rastra (null, gdy layout jest zdegenerowany). */
  const doRastra = (e: { clientX: number; clientY: number }): [number, number] | null => {
    const el = imgRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return null;
    const x = ((e.clientX - r.left) / r.width) * width;
    const y = ((e.clientY - r.top) / r.height) * height;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return [Math.max(0, Math.min(width, x)), Math.max(0, Math.min(height, y))];
  };

  // Przeciąganie obsługujemy na oknie, nie na elemencie — inaczej szybki ruch myszą „gubi" uchwyt.
  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent) => {
      const p = doRastra(e);
      if (!p) return;
      if (drag.typ === 'gcp') { onMoveGcp(drag.index, p[0], p[1]); return; }
      const c = drag.start;
      if (drag.uchwyt === 'move') {
        const dx = p[0] - drag.sx, dy = p[1] - drag.sy;
        onCrop({
          x: Math.max(0, Math.min(width - c.w, c.x + dx)),
          y: Math.max(0, Math.min(height - c.h, c.y + dy)),
          w: c.w, h: c.h,
        });
        return;
      }
      const x1 = drag.uchwyt === 'nw' || drag.uchwyt === 'sw' ? p[0] : c.x;
      const y1 = drag.uchwyt === 'nw' || drag.uchwyt === 'ne' ? p[1] : c.y;
      const x2 = drag.uchwyt === 'ne' || drag.uchwyt === 'se' ? p[0] : c.x + c.w;
      const y2 = drag.uchwyt === 'sw' || drag.uchwyt === 'se' ? p[1] : c.y + c.h;
      const nx = Math.min(x1, x2), ny = Math.min(y1, y2);
      onCrop({ x: nx, y: ny, w: Math.max(20, Math.abs(x2 - x1)), h: Math.max(20, Math.abs(y2 - y1)) });
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  const klik = (e: React.MouseEvent) => {
    if (drag) return;
    const p = doRastra(e);
    if (!p) return;
    if (tryb === 'gcp') onAddGcp(p[0], p[1]);
    else if (tryb === 'trace') onObrys([...obrys, p]);
  };

  const r = Math.max(4, width / 220);          // promień znacznika w pikselach rastra
  const gruboscKreski = Math.max(1.5, width / 700);
  const c = crop ?? { x: 0, y: 0, w: width, h: height };

  return (
    <div className="relative overflow-auto border border-ink-200 rounded bg-ink-50 max-h-[68vh]">
      <div className="relative" style={{ width: `${zoom * 100}%` }}>
        <img
          ref={imgRef}
          src={rasterUrl}
          alt="raster mapy"
          className="block w-full select-none"
          draggable={false}
          onClick={klik}
          style={{ cursor: tryb === 'crop' ? 'default' : 'crosshair' }}
        />
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          style={{ pointerEvents: 'none' }}
        >
          {/* przyciemnienie poza polem mapy — od razu widać, co wypadnie z warstwy */}
          <path
            d={`M0,0 H${width} V${height} H0 Z M${c.x},${c.y} V${c.y + c.h} H${c.x + c.w} V${c.y} Z`}
            fill="rgba(15,23,42,0.45)"
            fillRule="evenodd"
          />
          <rect x={c.x} y={c.y} width={c.w} height={c.h} fill="none" stroke="#06b6d4" strokeWidth={gruboscKreski * 1.5} />

          {tryb === 'crop' && (
            <>
              <rect
                x={c.x} y={c.y} width={c.w} height={c.h} fill="transparent"
                style={{ pointerEvents: 'all', cursor: 'move' }}
                onMouseDown={e => {
                  const p = doRastra(e); if (!p) return;
                  setDrag({ typ: 'crop', uchwyt: 'move', start: c, sx: p[0], sy: p[1] });
                }}
              />
              {([['nw', c.x, c.y], ['ne', c.x + c.w, c.y], ['se', c.x + c.w, c.y + c.h], ['sw', c.x, c.y + c.h]] as const)
                .map(([u, hx, hy]) => (
                  <rect
                    key={u}
                    x={hx - r * 1.6} y={hy - r * 1.6} width={r * 3.2} height={r * 3.2}
                    fill="#fff" stroke="#06b6d4" strokeWidth={gruboscKreski}
                    style={{ pointerEvents: 'all', cursor: u === 'nw' || u === 'se' ? 'nwse-resize' : 'nesw-resize' }}
                    onMouseDown={e => {
                      e.stopPropagation();
                      const p = doRastra(e); if (!p) return;
                      setDrag({ typ: 'crop', uchwyt: u, start: c, sx: p[0], sy: p[1] });
                    }}
                  />
                ))}
            </>
          )}

          {obrys.length > 0 && (
            <polygon
              points={obrys.map(p => `${p[0]},${p[1]}`).join(' ')}
              fill="rgba(124,92,255,0.20)" stroke="#7c5cff" strokeWidth={gruboscKreski * 1.5}
            />
          )}

          {gcps.map((g, i) => {
            const zly = (g.residualM ?? 0) > 25;
            const kolor = !g.active ? '#94a3b8' : zly ? '#dc2626' : g.source === 'MANUAL' ? '#0f766e' : '#b45309';
            return (
              <g key={g.id ?? `n${i}`} style={{ pointerEvents: tryb === 'gcp' ? 'all' : 'none' }}>
                <circle
                  cx={g.px} cy={g.py} r={wybranyGcp === i ? r * 1.5 : r}
                  fill={kolor} stroke="#fff" strokeWidth={gruboscKreski}
                  style={{ cursor: 'grab' }}
                  onMouseDown={e => { e.stopPropagation(); onWybierzGcp(i); setDrag({ typ: 'gcp', index: i }); }}
                />
                {/* krzyż celowniczy — środek symbolu widać także pod kółkiem */}
                <path d={`M${g.px - r * 2},${g.py} H${g.px + r * 2} M${g.px},${g.py - r * 2} V${g.py + r * 2}`}
                      stroke={kolor} strokeWidth={gruboscKreski * 0.8} opacity={0.9} />
                <text x={g.px + r * 2} y={g.py - r} fontSize={r * 3} fill={kolor} stroke="#fff" strokeWidth={r * 0.25}
                      paintOrder="stroke">
                  {g.label ?? `#${i + 1}`}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
